/**
 * MemberDirectory — full audience management for The Archive.
 *
 * Replaces the original `AudienceAnalyticsPanel` at /admin/members/audience.
 * The original analytics panel is preserved and now lives on a separate
 * sub-page (Members → Analytics).
 *
 * Features:
 *   • Search by callsign / name / email / designation (server-side)
 *   • Filters: status · tier · clearance · founding · newsletter · tags · activity
 *   • Bulk select (checkboxes) + bulk actions:
 *       - Send email (composer modal)
 *       - Tag add / remove
 *       - Clearance set (confirmation modal)
 *       - Archive / Restore (confirmation modal)
 *       - Export CSV (with current filters or selected ids)
 *   • Row click opens the existing MemberDossierDrawer (full profile)
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Search, Filter, Download, Mail, Tag, ShieldCheck, ArchiveX, ArchiveRestore,
  CheckSquare, Square, X, Star, Loader2, RefreshCw,
} from "lucide-react";
import {
  adminMembersList, adminMembersBulkTag, adminMembersBulkClearance,
  adminMembersBulkArchive, adminMembersBulkRestore, adminMembersBulkNewsletter,
  adminMembersExportCsvUrl, adminEmailHubSendBulk,
} from "../../lib/api";
import { isFoundingMember, FoundingBadge, foundingCallsignColor } from "../founding";
import { emitArchiveInvalidate, useArchiveInvalidate } from "../../lib/archive-events";

// We keep using AdminAssetDirectory for the dossier drawer (which has the
// full member profile, achievements, audit history, email history, danger
// zone, etc). The Directory view here focuses on the LIST + BULK layer.

const STATUSES = [
  { key: "active",   label: "Active" },
  { key: "archived", label: "Archived" },
  { key: "all",      label: "All" },
];

const NEWSLETTER_OPTS = [
  { key: "",          label: "All" },
  { key: "opted_in",  label: "Opted In" },
  { key: "opted_out", label: "Opted Out" },
];

export const MemberDirectory = ({ token, userToken }) => {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ active: 0, archived: 0, all: 0 });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("active");
  const [tier, setTier] = useState("");
  const [clearance, setClearance] = useState(new Set());
  const [founding, setFounding] = useState(null);    // null | true | false
  const [newsletter, setNewsletter] = useState("");
  const [tags, setTags] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [openMemberId, setOpenMemberId] = useState(null);

  // Bulk action modals
  const [bulkEmail, setBulkEmail] = useState(false);
  const [bulkClearance, setBulkClearance] = useState(false);
  const [bulkArchive, setBulkArchive] = useState(false);
  const [bulkRestore, setBulkRestore] = useState(false);
  const [bulkTag, setBulkTag] = useState(false);

  const filters = useMemo(() => ({
    search: search || undefined,
    status,
    tier: tier || undefined,
    clearance: clearance.size > 0 ? Array.from(clearance).join(",") : undefined,
    founding: founding === null ? undefined : founding,
    newsletter: newsletter || undefined,
    tags: tags ? tags.trim().toLowerCase() : undefined,
    limit: 500,
  }), [search, status, tier, clearance, founding, newsletter, tags]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminMembersList(token, filters, userToken);
      setRows(r.rows || []);
      setCounts(r.counts || { active: 0, archived: 0, all: 0 });
    } catch { toast.error("Failed to load members"); }
    finally { setLoading(false); }
  }, [token, userToken, filters]);

  useEffect(() => { load(); }, [load]);
  useArchiveInvalidate("members", load);

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  };
  const toggleRow = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const selectedIds = Array.from(selected);
  const selectedRows = rows.filter((r) => selected.has(r.id));

  const exportCsv = () => {
    const url = adminMembersExportCsvUrl(token, {
      ...(selected.size > 0 ? { member_ids: selectedIds.join(",") } : filters),
    });
    window.open(url, "_blank");
  };

  return (
    <div data-testid="member-directory">
      <div className="flex items-end justify-between gap-3 mb-3">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2">// AUDIENCE</div>
          <h1 className="font-display text-4xl text-[#E4DFD3]">Member Directory</h1>
          <p className="text-[#A39E93] mt-1 text-sm max-w-2xl">
            Search, filter, bulk-edit, and export every Archive member. Click a
            row for the full member dossier (profile, achievements, audit
            history, email history, danger zone).
          </p>
        </div>
        <button onClick={load} className="flex items-center gap-2 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]" data-testid="dir-refresh">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Filters bar */}
      <div className="border border-[#2D2A26] bg-[#0a0a0a] p-4 mb-4 space-y-3" data-testid="dir-filters">
        <div className="grid sm:grid-cols-3 lg:grid-cols-4 gap-2">
          <div className="flex items-center gap-2 border border-[#2D2A26] bg-[#0F0F0F] px-3 py-2">
            <Search size={12} className="text-[#757065]" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search callsign / email / designation" data-testid="dir-search"
              className="flex-1 bg-transparent outline-none text-sm text-[#E4DFD3] placeholder:text-[#757065]" />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="dir-status"
            className="bg-[#0F0F0F] border border-[#2D2A26] outline-none px-3 py-2 font-mono text-xs text-[#E4DFD3]">
            {STATUSES.map((o) => {
              const lbl = `${o.label} · ${counts[o.key] ?? 0}`;
              return <option key={o.key} value={o.key}>{lbl}</option>;
            })}
          </select>
          <select value={tier} onChange={(e) => setTier(e.target.value)} data-testid="dir-tier"
            className="bg-[#0F0F0F] border border-[#2D2A26] outline-none px-3 py-2 font-mono text-xs text-[#E4DFD3]">
            <option value="">All Tiers</option>
            <option value="subscriber">Subscriber</option>
            <option value="member">Member</option>
          </select>
          <select value={newsletter} onChange={(e) => setNewsletter(e.target.value)} data-testid="dir-newsletter"
            className="bg-[#0F0F0F] border border-[#2D2A26] outline-none px-3 py-2 font-mono text-xs text-[#E4DFD3]">
            {NEWSLETTER_OPTS.map((o) => {
              const lbl = `NL: ${o.label}`;
              return <option key={o.key} value={o.key}>{lbl}</option>;
            })}
          </select>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#757065]">CLEARANCE:</span>
          {[1, 2, 3, 4, 5].map((n) => {
            const on = clearance.has(n);
            return (
              <button key={n} onClick={() => {
                setClearance((s) => { const n2 = new Set(s); if (n2.has(n)) n2.delete(n); else n2.add(n); return n2; });
              }} data-testid={`dir-clearance-${n}`}
                className={"px-2 py-1 border font-mono text-[10px] " +
                  (on ? "border-[#E0A526] text-[#E0A526] bg-[#1a0808]" : "border-[#2D2A26] text-[#A39E93]")}>{n}★</button>
            );
          })}
          <span className="ml-3 font-mono text-[9px] uppercase tracking-[0.2em] text-[#757065]">FOUNDING:</span>
          {[
            { v: null, l: "Any" }, { v: true, l: "Yes" }, { v: false, l: "No" },
          ].map((o) => (
            <button key={String(o.v)} onClick={() => setFounding(o.v)} data-testid={`dir-founding-${o.v ?? 'any'}`}
              className={"px-2 py-1 border font-mono text-[10px] uppercase tracking-[0.2em] " +
                (founding === o.v ? "border-[#E0A526] text-[#E0A526] bg-[#1a0808]" : "border-[#2D2A26] text-[#A39E93]")}>{o.l}</button>
          ))}
          <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="tags (comma list)" data-testid="dir-tags"
            className="ml-3 bg-[#0F0F0F] border border-[#2D2A26] outline-none px-2 py-1 font-mono text-xs text-[#E4DFD3] placeholder:text-[#757065] w-44" />
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="border border-[#D92D20]/40 bg-[#1a0808] p-3 mb-3 flex flex-wrap items-center gap-2" data-testid="dir-bulk-bar">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">// {selected.size} SELECTED</span>
          <button onClick={() => setBulkEmail(true)} data-testid="bulk-email-btn" className="flex items-center gap-1 border border-[#D92D20]/50 hover:bg-[#D92D20]/15 text-[#D92D20] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em]"><Mail size={11} /> Email</button>
          <button onClick={() => setBulkTag(true)} data-testid="bulk-tag-btn" className="flex items-center gap-1 border border-[#9FA8D4]/50 hover:bg-[#9FA8D4]/15 text-[#9FA8D4] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em]"><Tag size={11} /> Tag</button>
          <button onClick={() => setBulkClearance(true)} data-testid="bulk-clearance-btn" className="flex items-center gap-1 border border-[#E0A526]/50 hover:bg-[#E0A526]/15 text-[#E0A526] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em]"><ShieldCheck size={11} /> Clearance</button>
          <button onClick={() => setBulkArchive(true)} data-testid="bulk-archive-btn" className="flex items-center gap-1 border border-[#A39E93]/50 hover:bg-[#A39E93]/15 text-[#A39E93] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em]"><ArchiveX size={11} /> Archive</button>
          <button onClick={() => setBulkRestore(true)} data-testid="bulk-restore-btn" className="flex items-center gap-1 border border-[#5DD4B3]/50 hover:bg-[#5DD4B3]/15 text-[#5DD4B3] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em]"><ArchiveRestore size={11} /> Restore</button>
          <button onClick={exportCsv} data-testid="bulk-export-btn" className="flex items-center gap-1 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em]"><Download size={11} /> CSV</button>
          <button onClick={() => setSelected(new Set())} className="ml-auto text-[#A39E93] hover:text-[#D92D20]" data-testid="bulk-clear-selection"><X size={12} /></button>
        </div>
      )}

      {selected.size === 0 && (
        <div className="flex items-center gap-2 mb-3">
          <button onClick={exportCsv} data-testid="dir-export-all" className="flex items-center gap-1 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em]">
            <Download size={11} /> Export filtered ({rows.length}) as CSV
          </button>
        </div>
      )}

      {/* Table */}
      <div className="border border-[#2D2A26] bg-[#0a0a0a] overflow-x-auto">
        <table className="min-w-full text-sm" data-testid="dir-table">
          <thead>
            <tr className="border-b border-[#2D2A26] bg-[#0F0F0F]">
              <th className="px-3 py-2 text-left">
                <button onClick={toggleAll} className="text-[#A39E93] hover:text-[#D92D20]" data-testid="dir-select-all">
                  {allSelected ? <CheckSquare size={14} /> : <Square size={14} />}
                </button>
              </th>
              <th className="px-3 py-2 text-left font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">Callsign</th>
              <th className="px-3 py-2 text-left font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">Email</th>
              <th className="px-3 py-2 text-left font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">Tier</th>
              <th className="px-3 py-2 text-left font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">Clearance</th>
              <th className="px-3 py-2 text-left font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">Reads</th>
              <th className="px-3 py-2 text-left font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">Newsletter</th>
              <th className="px-3 py-2 text-left font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">Joined</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={8} className="px-3 py-10 text-center font-mono text-xs text-[#757065]">No members match.</td></tr>
            )}
            {rows.map((r) => {
              const founding = !!r.is_founding || (r.designation || "").toLowerCase() === "founding member";
              const stars = r.clearance?.rank || Math.max(0, Math.min(5, Number(r.clearance_level || 0)));
              const np = r.newsletter_prefs || {};
              const nlOn = !!(np.weekly_brief || np.major_releases);
              return (
                <tr key={r.id} className={"border-b border-[#2D2A26]/60 hover:bg-[#131313] " + (selected.has(r.id) ? "bg-[#1a0808]" : "")}
                  data-testid={`dir-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <button onClick={() => toggleRow(r.id)} className="text-[#A39E93] hover:text-[#D92D20]" data-testid={`dir-row-select-${r.id}`}>
                      {selected.has(r.id) ? <CheckSquare size={14} /> : <Square size={14} />}
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    <button onClick={() => setOpenMemberId(r.id)} data-testid={`dir-row-open-${r.id}`}
                      className="text-left">
                      <span className="font-display text-base hover:underline" style={{ color: foundingCallsignColor(founding) }}>{r.callsign || "—"}</span>
                      {founding && <FoundingBadge size="sm" testid={`dir-row-founding-${r.id}`} />}
                      <div className="font-mono text-[10px] text-[#757065]">{r.designation || ""}</div>
                    </button>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-[#A39E93]">{r.email || "—"}</td>
                  <td className="px-3 py-2"><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9FA8D4]">{(r.tier || "subscriber").toUpperCase()}</span></td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-0.5" data-testid={`dir-row-clearance-${r.id}`}>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star key={n} size={11} fill={n <= stars ? "#E0A526" : "transparent"} color={n <= stars ? "#E0A526" : "#454038"} />
                      ))}
                      <span className="ml-1 font-mono text-[9px] text-[#757065]">{r.clearance?.label}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-[#A39E93]">{r.dossiers_read ?? 0}</td>
                  <td className="px-3 py-2">
                    <span className={"font-mono text-[10px] " + (nlOn ? "text-[#5DD4B3]" : "text-[#757065]")}>{nlOn ? "ON" : "OFF"}</span>
                  </td>
                  <td className="px-3 py-2 font-mono text-[10px] text-[#757065]">{r.joined_at ? new Date(r.joined_at).toLocaleDateString() : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modals */}
      {bulkEmail && <BulkEmailModal token={token} userToken={userToken} ids={selectedIds} onDone={() => { setBulkEmail(false); setSelected(new Set()); load(); }} />}
      {bulkTag && <BulkTagModal token={token} userToken={userToken} ids={selectedIds} onDone={() => { setBulkTag(false); load(); }} />}
      {bulkClearance && <BulkClearanceModal token={token} userToken={userToken} ids={selectedIds} onDone={() => { setBulkClearance(false); load(); }} />}
      {bulkArchive && <BulkArchiveModal token={token} userToken={userToken} ids={selectedIds} onDone={() => { setBulkArchive(false); setSelected(new Set()); load(); }} />}
      {bulkRestore && <BulkRestoreModal token={token} userToken={userToken} ids={selectedIds} onDone={() => { setBulkRestore(false); setSelected(new Set()); load(); }} />}

      {/* Open the full member dossier in a navigated view to reuse the
          existing AdminAssetDirectory drawer + danger zone + email history
          + audit history components (single source of truth). */}
      {openMemberId && (
        <DossierLink memberId={openMemberId} onClose={() => { setOpenMemberId(null); load(); }} />
      )}
    </div>
  );
};

import { useNavigate } from "react-router-dom";

const DossierLink = ({ memberId, onClose }) => {
  const nav = useNavigate();
  useEffect(() => {
    nav(`/admin/dossiers/asset-directory#member=${memberId}`);
    onClose();
  }, [memberId, nav, onClose]);
  return null;
};

// =============== Bulk modals ============================================

const BulkEmailModal = ({ token, userToken, ids, onDone }) => {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!subject.trim() || !body.trim()) return toast.error("Subject and body required");
    if (!window.confirm(`Send to ${ids.length} member${ids.length === 1 ? "" : "s"}?`)) return;
    setBusy(true);
    try {
      const r = await adminEmailHubSendBulk(token, {
        subject, body,
        audience: { explicit_member_ids: ids, newsletter_status: "either" },
      }, userToken);
      toast.success(`Sent: ${r.sent} · Failed: ${r.failed} · Disabled: ${r.disabled}`);
      emitArchiveInvalidate(["emails", "members"]);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title="Bulk Email" onClose={onDone} testid="bulk-email-modal">
      <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" data-testid="bulk-email-subject"
        className="w-full bg-[#0a0a0a] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-sm text-[#E4DFD3] mb-3" />
      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} placeholder="Body" data-testid="bulk-email-body"
        className="w-full bg-[#0a0a0a] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-sm text-[#E4DFD3] resize-y mb-3" />
      <div className="flex gap-2">
        <button onClick={send} disabled={busy} data-testid="bulk-email-send" className="flex-1 bg-[#D92D20] hover:bg-[#B92318] text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] disabled:opacity-40">
          {busy ? <Loader2 size={12} className="animate-spin inline" /> : "Send"}
        </button>
        <button onClick={onDone} className="px-3 py-2 border border-[#454038] text-[#A39E93] font-mono text-[10px] uppercase tracking-[0.25em]">Cancel</button>
      </div>
    </ModalShell>
  );
};

const BulkTagModal = ({ token, userToken, ids, onDone }) => {
  const [addText, setAddText] = useState("");
  const [removeText, setRemoveText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const add = addText.split(",").map((t) => t.trim()).filter(Boolean);
    const remove = removeText.split(",").map((t) => t.trim()).filter(Boolean);
    if (!add.length && !remove.length) return toast.error("Specify add or remove tags");
    setBusy(true);
    try {
      const r = await adminMembersBulkTag(token, { member_ids: ids, add_tags: add, remove_tags: remove }, userToken);
      toast.success(`${r.matched} updated`);
      emitArchiveInvalidate("members");
      onDone();
    } catch { toast.error("Bulk tag failed"); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title="Bulk Tag" onClose={onDone} testid="bulk-tag-modal">
      <input value={addText} onChange={(e) => setAddText(e.target.value)} placeholder="Add tags (comma-separated)" data-testid="bulk-tag-add"
        className="w-full bg-[#0a0a0a] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-sm text-[#E4DFD3] mb-2" />
      <input value={removeText} onChange={(e) => setRemoveText(e.target.value)} placeholder="Remove tags (comma-separated)" data-testid="bulk-tag-remove"
        className="w-full bg-[#0a0a0a] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-sm text-[#E4DFD3] mb-3" />
      <div className="flex gap-2">
        <button onClick={submit} disabled={busy} data-testid="bulk-tag-apply" className="flex-1 bg-[#D92D20] hover:bg-[#B92318] text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] disabled:opacity-40">Apply</button>
        <button onClick={onDone} className="px-3 py-2 border border-[#454038] text-[#A39E93] font-mono text-[10px] uppercase tracking-[0.25em]">Cancel</button>
      </div>
    </ModalShell>
  );
};

const BulkClearanceModal = ({ token, userToken, ids, onDone }) => {
  const [level, setLevel] = useState(1);
  const [busy, setBusy] = useState(false);
  const apply = async () => {
    if (!window.confirm(`Set clearance to ${level}★ for ${ids.length} member${ids.length === 1 ? "" : "s"}? This will override admin-pinned values.`)) return;
    setBusy(true);
    try {
      const r = await adminMembersBulkClearance(token, { member_ids: ids, clearance_level: level, confirm: "APPLY" }, userToken);
      toast.success(`${r.matched} updated to ${level}★`);
      emitArchiveInvalidate(["members", "*"]);
      onDone();
    } catch { toast.error("Bulk clearance failed"); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title="Bulk Clearance" onClose={onDone} testid="bulk-clearance-modal">
      <p className="text-xs text-[#A39E93] mb-3">Confirmation required. Sets `clearance_level` for {ids.length} member{ids.length === 1 ? "" : "s"}.</p>
      <div className="flex gap-1 mb-3">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} onClick={() => setLevel(n)} data-testid={`bulk-clearance-level-${n}`}
            className={"px-3 py-2 border font-mono text-sm " +
              (level === n ? "border-[#E0A526] text-[#E0A526] bg-[#1a0808]" : "border-[#2D2A26] text-[#A39E93]")}>{n}★</button>
        ))}
      </div>
      <div className="flex gap-2">
        <button onClick={apply} disabled={busy} data-testid="bulk-clearance-apply" className="flex-1 bg-[#D92D20] hover:bg-[#B92318] text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] disabled:opacity-40">Apply</button>
        <button onClick={onDone} className="px-3 py-2 border border-[#454038] text-[#A39E93] font-mono text-[10px] uppercase tracking-[0.25em]">Cancel</button>
      </div>
    </ModalShell>
  );
};

const BulkArchiveModal = ({ token, userToken, ids, onDone }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const apply = async () => {
    if (!window.confirm(`Archive ${ids.length} member${ids.length === 1 ? "" : "s"}? They will be suppressed and their sessions killed.`)) return;
    setBusy(true);
    try {
      const r = await adminMembersBulkArchive(token, { member_ids: ids, reason, confirm: "APPLY" }, userToken);
      toast.success(`${r.archived} archived`);
      emitArchiveInvalidate("members");
      onDone();
    } catch { toast.error("Bulk archive failed"); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title="Bulk Archive" onClose={onDone} testid="bulk-archive-modal">
      <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Optional reason" rows={4} data-testid="bulk-archive-reason"
        className="w-full bg-[#0a0a0a] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-sm text-[#E4DFD3] mb-3" />
      <div className="flex gap-2">
        <button onClick={apply} disabled={busy} data-testid="bulk-archive-apply" className="flex-1 bg-[#D92D20] hover:bg-[#B92318] text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] disabled:opacity-40">Archive</button>
        <button onClick={onDone} className="px-3 py-2 border border-[#454038] text-[#A39E93] font-mono text-[10px] uppercase tracking-[0.25em]">Cancel</button>
      </div>
    </ModalShell>
  );
};

const BulkRestoreModal = ({ token, userToken, ids, onDone }) => {
  const [busy, setBusy] = useState(false);
  const apply = async () => {
    setBusy(true);
    try {
      const r = await adminMembersBulkRestore(token, { member_ids: ids }, userToken);
      toast.success(`${r.restored} restored`);
      emitArchiveInvalidate("members");
      onDone();
    } catch { toast.error("Bulk restore failed"); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title="Bulk Restore" onClose={onDone} testid="bulk-restore-modal">
      <p className="text-xs text-[#A39E93] mb-3">Restore {ids.length} archived member{ids.length === 1 ? "" : "s"}? Newsletter prefs stay off until re-enabled.</p>
      <div className="flex gap-2">
        <button onClick={apply} disabled={busy} data-testid="bulk-restore-apply" className="flex-1 bg-[#5DD4B3] hover:bg-[#4dc1a2] text-[#0a0907] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] disabled:opacity-40">Restore</button>
        <button onClick={onDone} className="px-3 py-2 border border-[#454038] text-[#A39E93] font-mono text-[10px] uppercase tracking-[0.25em]">Cancel</button>
      </div>
    </ModalShell>
  );
};

const ModalShell = ({ title, onClose, children, testid }) => (
  <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid={testid}>
    <div className="w-full max-w-md border border-[#D92D20]/40 bg-[#0F0F0F] p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">// {title}</div>
        <button onClick={onClose} className="text-[#A39E93] hover:text-[#D92D20]"><X size={14} /></button>
      </div>
      {children}
    </div>
  </div>
);

export default MemberDirectory;
